// The Sessions and Dosage & Collaboration PDFs: text, not a screenshot of the
// page.
//
// Pure functions of the rows as displayed and the summary. It reads nothing
// from the DOM, so the file says the same thing whatever the screen is showing.
// jsPDF is imported on demand, as WeeklyReport.js does, so it stays out of the
// main bundle.

import { colors } from "utils";
import { LEVEL_COLORS, SOLO_BAND_LEVEL } from "./reportTableStyles";
import {
  CSV_HEADERS,
  averageLevelText,
  dosageParts,
  EM_DASH,
  headlineText,
  levelLabel,
  percentText,
  sessionDateParts,
  soloBand,
  soloTimeText,
} from "./studentReportTable";
import { periodCsvHeaders, weekMinutesText } from "./trendPeriods";
import {
  firmLevel,
  lastLessonText,
  proficiencyFilterLine,
  proficiencyText,
  questionLabel,
} from "./proficiencyStandards";

const PAGE_MARGIN = 40;
const TITLE_FONT_SIZE = 18;
const BODY_FONT_SIZE = 11;
const LINE_GAP = 18;

export const PDF_TEXT_COLOR = colors.nightlyWoods500;

// jsPDF's standard fonts encode WinAnsi only, and a single character outside it
// switches the whole string to a two-byte encoding those fonts print as
// garbage. Headlines are model-written prose that uses math symbols, so each
// common one gets an ASCII spelling and anything else unencodable becomes "?".
const ASCII_SPELLINGS = {
  "\u2212": "-",
  "\u2010": "-",
  "\u2011": "-",
  "\u2264": "<=",
  "\u2265": ">=",
  "\u2260": "!=",
  "\u2248": "~",
  "\u221A": "sqrt ",
  "\u03C0": "pi",
  "\u221E": "infinity",
  "\u2192": "->",
  "\u2190": "<-",
  "\u22C5": "*",
  "\u2219": "*",
  "\u2032": "'",
  "\u2033": '"',
};
// Tabs, carriage returns and the typographic spaces (en quad to hair space,
// narrow no-break, medium mathematical, the line and paragraph separators,
// ideographic) all read as one plain space.
const SPACE_VARIANTS = /[\t\r\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/;
const PLAIN_SPACE = " ";

// The vulgar fractions, spelled "1/3". After a digit they take a
// space, so "2⅓" reads "2 1/3" and never "21/3".
const FRACTIONS = {
  "\u00BC": "1/4",
  "\u00BD": "1/2",
  "\u00BE": "3/4",
  "\u2150": "1/7",
  "\u2151": "1/9",
  "\u2152": "1/10",
  "\u2153": "1/3",
  "\u2154": "2/3",
  "\u2155": "1/5",
  "\u2156": "2/5",
  "\u2157": "3/5",
  "\u2158": "4/5",
  "\u2159": "1/6",
  "\u215A": "5/6",
  "\u215B": "1/8",
  "\u215C": "3/8",
  "\u215D": "5/8",
  "\u215E": "7/8",
};
const FRACTION_SLASH = "\u2044";
const isDigit = (character) => character >= "0" && character <= "9";

// Superscripts the font cannot draw become caret form, one caret per run:
// "10⁻³" reads "10^-3". A run of only ² and ³ is left alone, since the
// font draws those two.
const SUPERSCRIPTS = {
  "\u2070": "0",
  "\u00B9": "1",
  "\u00B2": "2",
  "\u00B3": "3",
  "\u2074": "4",
  "\u2075": "5",
  "\u2076": "6",
  "\u2077": "7",
  "\u2078": "8",
  "\u2079": "9",
  "\u207A": "+",
  "\u207B": "-",
};
const SUPERSCRIPT_RUN = new RegExp(`[${Object.keys(SUPERSCRIPTS).join("")}]+`, "g");
const DRAWN_SUPERSCRIPTS = /^[\u00B2\u00B3]+$/;

const caretForm = (text) =>
  text.replace(SUPERSCRIPT_RUN, (run) =>
    DRAWN_SUPERSCRIPTS.test(run) ? run : `^${[...run].map((mark) => SUPERSCRIPTS[mark]).join("")}`
  );

const WIN_ANSI_EXTRAS = "ŒœŠšŸŽžƒˆ˜–—‘’‚“”„†‡•…‰‹›€™";
const UNPRINTABLE = "?";

const isWinAnsi = (character) => {
  const code = character.codePointAt(0);
  return (
    character === "\n" ||
    (code >= 0x20 && code <= 0x7e) ||
    (code >= 0xa0 && code <= 0xff) ||
    WIN_ANSI_EXTRAS.includes(character)
  );
};

const printable = (character, previous) => {
  if (SPACE_VARIANTS.test(character)) return PLAIN_SPACE;
  if (FRACTIONS[character]) return `${isDigit(previous) ? " " : ""}${FRACTIONS[character]}`;
  if (character === FRACTION_SLASH) return "/";
  return ASCII_SPELLINGS[character] ?? (isWinAnsi(character) ? character : UNPRINTABLE);
};

// Emoji carry no meaning a teacher needs in a printed report, and the font
// cannot draw them, so the PDF drops them rather than print "?": the
// emoji-style pictographs, a text-style one that U+FE0F turns into an emoji
// (so a lone ☐ still prints "?"), and the selectors and joiners around them.
const EMOJI = /\p{Extended_Pictographic}\uFE0F|\p{Emoji_Presentation}|[\uFE0E\uFE0F\u200D]/gu;

// C0 and C1 control characters, except tab, newline and carriage return, which
// already read as whitespace. A corrupted apostrophe arrives as one of these.
const CONTROLS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g;
const INVISIBLES = /[\u200B\uFEFF]/g;
const REMOVED = [EMOJI, CONTROLS, INVISIBLES];

const withoutRemoved = (text) => REMOVED.reduce((kept, pattern) => kept.replace(pattern, ""), text);

// What a removal leaves behind: a doubled space, or one at the end of a line.
const tidySpaces = (text) => text.replace(/ {2,}/g, " ").replace(/^ +| +$/gm, "");

export const pdfSafeText = (text) =>
  tidySpaces(
    [...caretForm(withoutRemoved(String(text)))]
      .map((character, index, all) => printable(character, all[index - 1]))
      .join("")
  );

// A pill is drawn as its text in the pill's own text color; an
// unknown has no pill, so it keeps the page's color.
//
// The column is named by its header when the cell is drawn, not indexed when
// this module loads: `utils` reaches this file through the routes while
// studentReportTable.js is still loading, so its exports are not there yet.
const pillLevel = (row, column) => {
  const header = CSV_HEADERS[column];
  if (header === "Solo Accuracy") return SOLO_BAND_LEVEL[soloBand(row)];
  if (header === "Collaboration Level") return row.student_collab_level;
  return null;
};

const pillColor = (row, column) => LEVEL_COLORS[pillLevel(row, column)]?.fg || null;

// The Summary strip's four tiles, read from the same values and formatters.
const summaryLines = (summary = {}) => {
  const dosage = dosageParts(summary.total_minutes);
  return [
    `Sessions: ${summary.total_sessions || 0}`,
    `Dosage: ${dosage ? dosage.map((part) => `${part.value}${part.unit}`).join(" ") : EM_DASH}`,
    `Solo Accuracy: ${percentText(summary.solo_accuracy_pct)}`,
    `Avg. Collaboration: ${averageLevelText(summary.average_collab_level)}`,
  ];
};

// One table row, as the screen reads it but with nothing clamped.
const tableRow = (row, focusLabels) =>
  [
    sessionDateParts(row.started_at).date,
    focusLabels?.[row.session_id] || row.standard_prefix || EM_DASH,
    soloTimeText(row),
    headlineText(row.student_math_rationale),
    levelLabel(row.student_collab_level),
    headlineText(row.student_collab_rationale),
  ].map(pdfSafeText);

/**
 * A Student Reports PDF: the title block, the Summary, then one table. `head`
 * is the header row, `body` the rows as they are displayed, and
 * `cellColor(rowIndex, columnIndex)` names the text colour of a body cell, or
 * null to keep the page's own. `afterSummary` lines, if any, sit between the
 * Summary and the table. `cellDrawer(rowIndex, columnIndex)` may return a
 * function `(doc, cell)` that draws a body cell itself, in place of its text;
 * the text still sizes the column. `columnStyles` go to the table as given.
 */
export const buildReportPdf = async ({
  studentName,
  className,
  rangeText,
  summary,
  head,
  body,
  cellColor = () => null,
  afterSummary = [],
  cellDrawer = () => null,
  columnStyles = {},
}) => {
  const [{ default: JsPdf }, { autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const doc = new JsPdf({ orientation: "landscape", unit: "pt", format: "a4" });

  let y = PAGE_MARGIN;
  doc.setFontSize(TITLE_FONT_SIZE);
  doc.text(pdfSafeText(`Student Reports: ${studentName}`), PAGE_MARGIN, y);
  doc.setFontSize(BODY_FONT_SIZE);
  [className, rangeText].filter(Boolean).forEach((line) => {
    y += LINE_GAP;
    doc.text(pdfSafeText(line), PAGE_MARGIN, y);
  });

  y += LINE_GAP * 2;
  doc.text("Summary:", PAGE_MARGIN, y);
  summaryLines(summary).forEach((line) => {
    y += LINE_GAP;
    doc.text(line, PAGE_MARGIN, y);
  });
  afterSummary.forEach((line, index) => {
    y += index === 0 ? LINE_GAP * 2 : LINE_GAP;
    doc.text(pdfSafeText(line), PAGE_MARGIN, y);
  });

  autoTable(doc, {
    startY: y + LINE_GAP,
    margin: PAGE_MARGIN,
    head: [head],
    body,
    styles: { fontSize: BODY_FONT_SIZE, textColor: PDF_TEXT_COLOR },
    columnStyles,
    rowPageBreak: "avoid",
    showHead: "everyPage",
    didParseCell: (data) => {
      if (data.section !== "body") return;
      const color = cellColor(data.row.index, data.column.index);
      if (color) data.cell.styles.textColor = color;
    },
    willDrawCell: (data) => {
      if (data.section === "body" && cellDrawer(data.row.index, data.column.index)) {
        data.cell.text = [];
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      cellDrawer(data.row.index, data.column.index)?.(doc, data.cell);
    },
  });

  return doc;
};

export const buildSessionsPdf = ({
  rows = [],
  summary,
  focusLabels = {},
  studentName,
  className,
  rangeText,
}) =>
  buildReportPdf({
    studentName,
    className,
    rangeText,
    summary,
    head: CSV_HEADERS,
    body: rows.map((row) => tableRow(row, focusLabels)),
    cellColor: (rowIndex, columnIndex) => pillColor(rows[rowIndex], columnIndex),
  });

// The Dosage & Collaboration table, one row per week as displayed. Its last
// column is the week's level, drawn in its pill's text colour.
const PERIOD_LEVEL_COLUMN = 3;

const periodRow = (period) =>
  [
    period.label,
    String(period.sessionCount),
    weekMinutesText(period.totalMinutes),
    levelLabel(period.level),
  ].map(pdfSafeText);

export const buildCollaborationTrendPdf = ({
  periods = [],
  summary,
  studentName,
  className,
  rangeText,
}) =>
  buildReportPdf({
    studentName,
    className,
    rangeText,
    summary,
    head: periodCsvHeaders(),
    body: periods.map(periodRow),
    cellColor: (rowIndex, columnIndex) =>
      columnIndex === PERIOD_LEVEL_COLUMN ? LEVEL_COLORS[periods[rowIndex].level]?.fg || null : null,
  });

// The Proficiency table, rows as displayed, after the Grade/Domain line. The
// Standard cell holds the code and the whole name: the PDF clamps nothing.
const PROFICIENCY_PDF_HEADERS = ["Standard", "Most Recent", "Proficiency", "Last 10 Questions"];
const PROFICIENCY_LEVEL_COLUMN = 2;
const PROFICIENCY_QUESTIONS_COLUMN = 3;

// Each answer is its Q label, then a filled circle with a tick or a cross, as
// the screen draws it. Drawn as shapes: the standard fonts have no tick glyph.
const MARK_RADIUS = 5;
const LABEL_TO_MARK = 3;
const MARK_TO_NEXT = 8;
const MARK_STROKE = 1.2;
// Spaces that take the room the drawn mark does, so the column is sized for
// the labels and their marks.
const MARK_ROOM = " ".repeat(7);

const drawMark = (doc, x, y, correct) => {
  doc.setFillColor(correct ? colors.green500 : colors.pink500);
  doc.circle(x, y, MARK_RADIUS, "F");
  doc.setDrawColor(colors.milkGlass500);
  doc.setLineWidth(MARK_STROKE);
  doc.setLineCap("round");
  if (correct) {
    doc.lines([[1.8, 1.8], [3.4, -3.6]], x - 2.6, y, [1, 1], "S");
  } else {
    doc.line(x - 2, y - 2, x + 2, y + 2);
    doc.line(x + 2, y - 2, x - 2, y + 2);
  }
};

const drawAttempts = (attempts) => (doc, cell) => {
  const y = cell.y + cell.height / 2;
  let x = cell.x + cell.padding("left");
  attempts.forEach((attempt) => {
    const label = questionLabel(attempt);
    doc.setTextColor(PDF_TEXT_COLOR);
    doc.text(label, x, y, { baseline: "middle" });
    x += doc.getTextWidth(label) + LABEL_TO_MARK + MARK_RADIUS;
    drawMark(doc, x, y, attempt.correct);
    x += MARK_RADIUS + MARK_TO_NEXT;
  });
};

const proficiencyRow = (row) => [
  ...[
    [row.standard_code, row.standard_name].filter(Boolean).join("\n"),
    lastLessonText(row.last_lesson_date),
    proficiencyText(row),
  ].map(pdfSafeText),
  (row.attempts || []).map((attempt) => `${questionLabel(attempt)}${MARK_ROOM}`).join(""),
];

/** The Proficiency table for `buildReportPdf`: a level in its pill's colour. */
export const proficiencyPdfTable = (rows = []) => ({
  head: PROFICIENCY_PDF_HEADERS,
  body: rows.map(proficiencyRow),
  cellColor: (rowIndex, columnIndex) =>
    columnIndex === PROFICIENCY_LEVEL_COLUMN ? LEVEL_COLORS[firmLevel(rows[rowIndex])]?.fg || null : null,
  cellDrawer: (rowIndex, columnIndex) =>
    columnIndex === PROFICIENCY_QUESTIONS_COLUMN ? drawAttempts(rows[rowIndex].attempts || []) : null,
  columnStyles: { [PROFICIENCY_QUESTIONS_COLUMN]: { cellWidth: "wrap" } },
});

export const buildProficiencyPdf = ({
  rows = [],
  summary,
  studentName,
  className,
  rangeText,
  grade,
  domainName,
}) =>
  buildReportPdf({
    studentName,
    className,
    rangeText,
    summary,
    afterSummary: [proficiencyFilterLine({ grade, domainName })],
    ...proficiencyPdfTable(rows),
  });
